import { SeriesModel } from '@modules/entertainment/models/series.model.ts';
import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import { seriesColumns } from './series-table-component/columns/columns.tsx';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import EditDialogForm from '../series-dialog-content/series-dialog-content.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import {
  SeriesRowDataProps,
  SeriesTabsEnum
} from '@modules/entertainment/models/series.model.ts';
import { getSeries } from '@shared/api/entertainment-api/series-api.ts';
import useSeriesStore from '../../store/series.store.ts';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model.ts';
import { useSearchParams } from 'react-router-dom';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { getRowIdKey } from '@utils/table-selection.utils.tsx';

const SeriesTable: React.FC = () => {
  const {
    deleteRow,
    editRow,
    createRow,
    isLoading,
    getGenreList,
    genreList,
    resetSeasonsList,
    resetEpisodesList
  } = useSeriesStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedKeys: selectedRows,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<SeriesModel>({
    tableStore,
    getRowKey: getRowIdKey
  });
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [view, setView] = useState(ViewControlsEnum.List);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SeriesRowDataProps | null>(
    null
  );

  const [currentDisplayTab, setCurrentDisplayTab] = React.useState<string>(
    SeriesTabsEnum.Details
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const count = selectedRows.length;

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItem(row);
  };

  const handleDeleteConfirm = () => {
    deleteRow({
      id: selectedItem?._id ? selectedItem._id : selectedRows,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleSave = async (row: any) => {
    if (selectedItem?._id) {
      editRow({
        data: row,
        id: selectedItem?._id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      setCurrentDisplayTab(SeriesTabsEnum.Media);
      const res = await createRow({ data: row, state: tableStore.getState() });

      if (res?.data) {
        const mappedGenres =
          res.data.genres?.map((g: string) => {
            const found = genreList.find((item) => item.id === g);
            return found || g;
          }) || [];

        setSelectedItem({
          ...res.data,
          genres: mappedGenres
        });
      }
    }
  };

  const handleClose = () => {
    resetAllSelectedMedia();
    resetSeasonsList();
    resetEpisodesList();
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setSelectedItem(null);
    clearSelection();
    setCurrentDisplayTab(SeriesTabsEnum.Details);
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
      case ViewControlsEnum.ImportPlaylist:
        break;
      case ViewControlsEnum.SelectAll:
        break;
      case ViewControlsEnum.Search:
        break;
      case ViewControlsEnum.Language:
        setLanguage(action.value as LanguageEnum);
        break;
    }
  };

  const columns = seriesColumns(
    handleEdit,
    handleDelete,
    {
      handleSelectRow,
      handleSelectAll,
      isPageAllSelected,
      isPagePartiallySelected,
      isRowSelected
    },
    language
  );

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      getGenreList();

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        setOpenDialog(true);
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls onAction={handleAction} selectedRows={selectedRows} />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getSeries}
            columns={columns}
            language={language}
            onTriggerAction={handleTableTrigger}
            tableKey="entertainment-series-table"
            initialState={{
              sort: ['createdAt', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          title={t('entertainment_series_title')}
          size="xl"
          content={
            <EditDialogForm
              selectedItem={selectedItem}
              handleClose={handleClose}
              handleCreateOrUpdate={handleSave}
              currentDisplayTab={currentDisplayTab}
              setCurrentDisplayTab={setCurrentDisplayTab}
            />
          }
          onClose={handleClose}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isLoading()}
          title={
            selectedItem?._id
              ? t('delete_popup_title')
              : t('delete_popup_title_many_items', { count })
          }
        />
      </div>
    </div>
  );
};

export default SeriesTable;
